import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mark, ResultChip } from '../primitives';
import { money, pct } from '../lib/format';
import './BestBetTile.css';
import { useTrack, useTrackExpand } from '../track';

/** One best bet or value bet, as shown on the home page and on a meeting's tips page. */
export interface BestBet {
  kind: 'best' | 'value';
  /** 1-based position among the day's best bets (ignored for value bets). */
  index: number;
  track: string;
  race: number;
  number: number;
  horse: string;
  /** Market price. */
  price: number | null;
  /** Start time as shown ("2:20pm"), used before the race has run. */
  time?: string;
  /** Selection both analyses agree on (shows the brand's mark). */
  edge: boolean;
  /** Finishing position once run; null or undefined before. */
  position?: number | null;
  sp?: number | null;
  why?: string | null;
  /** Our chance of winning, 0-100. */
  chance?: number | null;
  ourPrice?: number | null;
  /** Value bets: how far the market is over our price, in %. */
  overlay?: number | null;
  /** The race on its meeting's tips page; tapping the card goes there. */
  href?: string;
}

/** Best-bet card: tap it to go to the race, or the arrow for our reasoning, chance and prices. Look comes from data-bestbet. */
export function BestBetTile({ bet }: { bet: BestBet }) {
  const [open, setOpen] = useState(false);
  const expand = useTrackExpand();
  const track = useTrack();
  const label = bet.kind === 'value' ? 'Value bet' : `Best bet ${bet.index}`;
  const ran = bet.position != null && bet.position > 0 && bet.position !== 999;
  const body = (
    <>
      <div className="main">
        <div className="k">
          <span className="bt">{label}</span>
          {bet.track} R{bet.race}
        </div>
        <div className="n">
          #{bet.number} {bet.horse}
        </div>
      </div>
      <div className="r">
        {ran ? <ResultChip pos={bet.position} sp={bet.sp} /> : bet.time ? <ResultChip pending={bet.time} /> : null}
        <span className="odds">{money(bet.price)}</span>
        <Mark show={bet.edge} />
      </div>
    </>
  );
  return (
    <div className={`ui-bbt ${bet.kind}`} data-open={open}>
      <div className="row">
        {bet.href ? (
          <Link
            className="go"
            to={bet.href}
            title={`Go to ${bet.track} race ${bet.race}`}
            onClick={() => track('ui_click', { target: 'best_bet', kind: bet.kind, index: bet.index, trackName: bet.track, raceNumber: bet.race })}
          >
            {body}
          </Link>
        ) : (
          <div className="go">{body}</div>
        )}
        <button
          type="button"
          className="x"
          aria-expanded={open}
          aria-label="Why we like it"
          title="Why we like it"
          onClick={() => {
            setOpen(!open);
            expand('best_bet', !open, { kind: bet.kind, index: bet.index, trackName: bet.track, raceNumber: bet.race });
          }}
        >
          <span className="chev" aria-hidden="true">
            ▾
          </span>
        </button>
      </div>
      <div className="more">
        {bet.why && (
          <>
            <b>Why:</b> {bet.why}
            <br />
          </>
        )}
        <b>Chance:</b> {pct(bet.chance)} · <b>Our price:</b> {money(bet.ourPrice)} · <b>Market:</b> {money(bet.price)}
        {bet.kind === 'value' && bet.overlay != null && (
          <>
            {' '}
            · <b>Overlay:</b> +{bet.overlay.toFixed(0)}%
          </>
        )}
      </div>
    </div>
  );
}
